import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AGREEMENT } from "@/constants/testIds";
import { AlertTriangle, ShieldCheck, FileWarning, BadgeInfo, Lock, Timer } from "lucide-react";

const VALID_TIERS = ["basic", "premium"];
const PRICE_LOCK_SECONDS = 15 * 60; // 15 minutes — matches the countdown timer copy.

const REASON_PATTERNS = [
  [/^Launch promo · seat 1-(\d+) · FREE$/, "reasonPromoFree"],
  [/^Launch promo · seat (\d+-\d+) · \$([\d.]+)$/, "reasonPromoHalf"],
  [/^Full price$/, "reasonFullPrice"],
  [/^Loyalty upgrade · Basic -> Premium \(was \$(\d+)\)$/, "reasonLoyalty"],
  [/^Referral coupon · (\d+)% off \(saved \$(\d+)\)$/, "reasonReferral"],
];

function translateReason(reason, t) {
  for (const [re, key] of REASON_PATTERNS) {
    const m = reason.match(re);
    if (m) return t(`agreementUi.${key}`, { a: m[1], b: m[2], defaultValue: reason });
  }
  return reason;
}

function PromoCodeBox({ applied, quoteUrl, onApplied }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const apply = async (c) => {
    setBusy(true); setMsg(null);
    try {
      const { data } = await api.get(quoteUrl(c));
      if (c && data.code) { onApplied(data.code, data); setMsg({ ok: true, text: t("agreementUi.codeApplied", { code: data.code }) }); }
      else { onApplied("", data); if (c) setMsg({ ok: false, text: data.code_error || t("agreementUi.codeInvalid") }); }
    } catch { setMsg({ ok: false, text: t("agreementUi.codeInvalid") }); } finally { setBusy(false); }
  };
  if (!open && !applied) {
    return <button type="button" onClick={() => setOpen(true)} data-testid="agreement-have-code" className="mt-4 text-xs font-semibold text-blue-800 underline underline-offset-2">{t("agreementUi.haveCode")}</button>;
  }
  return (
    <div className="mt-4" data-testid="agreement-code-box">
      {applied ? (
        <div className="flex items-center gap-2 text-xs">
          <span className="font-bold text-emerald-700" data-testid="agreement-code-applied">{applied}</span>
          <button type="button" className="underline text-slate-500" onClick={() => { setCode(""); apply(""); }} data-testid="agreement-code-remove">{t("agreementUi.codeRemove")}</button>
        </div>
      ) : (
        <div className="flex gap-2">
          <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder={t("agreementUi.promoCodePlaceholder")} data-testid="agreement-code-input"
            className="h-9 flex-1 rounded-md border border-slate-300 px-3 text-sm uppercase tracking-wider" />
          <button type="button" disabled={busy || !code.trim()} onClick={() => apply(code.trim())} data-testid="agreement-code-apply"
            className="h-9 px-4 rounded-md bg-blue-900 text-white text-sm font-semibold disabled:opacity-50">{t("agreementUi.codeApply")}</button>
        </div>
      )}
      {msg && <p className={`mt-1.5 text-[11px] font-medium ${msg.ok ? "text-emerald-700" : "text-rose-600"}`} data-testid="agreement-code-msg">{msg.text}</p>}
    </div>
  );
}

export default function Agreement() {
  const { t } = useTranslation();
  const { tier } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [version, setVersion] = useState("");
  const [quote, setQuote] = useState(null);
  const [appliedCode, setAppliedCode] = useState("");
  const quoteUrl = (code = appliedCode) => `/checkout/quote?package_id=${tier}${code ? `&code=${encodeURIComponent(code)}` : ""}`;
  const [ackRisk, setAckRisk] = useState(false);
  const [ackNotAdvice, setAckNotAdvice] = useState(false);
  const [ackNoRefund, setAckNoRefund] = useState(false);
  const [fullName, setFullName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Peak referral code — grey `PMS-` prefix + user-typed 4-char tail.
  // Optional: if the user got a code from a friend but didn't click the
  // `?ref=` link, they can paste it here as a fail-safe.
  const [refTail, setRefTail] = useState("");
  const [refState, setRefState] = useState({ status: "idle" }); // idle | linking | linked | error

  const tierValid = VALID_TIERS.includes(tier);
  const tierLabel = tier ? t(`agreementUi.tier_${tier}`) : "";

  useEffect(() => {
    let alive = true;
    api.get("/agreement/current").then(({ data }) => {
      if (alive) setVersion(data.version);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // Ask the server for the exact price this user will pay (promo /
  // loyalty upgrade / referral coupon all resolved server-side). This is
  // the number displayed at the top of the agreement — the last moment
  // before "I agree", so it must match the eventual Stripe charge to the cent.
  const [priceChangedNotice, setPriceChangedNotice] = useState(false);
  const [lockExpiresAt, setLockExpiresAt] = useState(null);
  const [now, setNow] = useState(Date.now());

  const refetchQuote = () => {
    if (!tierValid || !user || typeof user !== "object") return Promise.resolve();
    return api.get(quoteUrl())
      .then(({ data }) => {
        setQuote((prev) => {
          if (prev && (prev.effective_amount !== data.effective_amount || prev.promo_stage !== data.promo_stage)) {
            setPriceChangedNotice(true);
          }
          return data;
        });
        setLockExpiresAt(Date.now() + PRICE_LOCK_SECONDS * 1000);
      })
      .catch(() => { setQuote(null); });
  };

  useEffect(() => {
    if (!tierValid || !user || typeof user !== "object") return;
    let alive = true;
    api.get(quoteUrl())
      .then(({ data }) => {
        if (!alive) return;
        setQuote(data);
        setLockExpiresAt(Date.now() + PRICE_LOCK_SECONDS * 1000);
      })
      .catch(() => { if (alive) setQuote(null); });
    return () => { alive = false; };
  }, [tier, tierValid, user]);

  // Tick the countdown every second while the lock is live. When it
  // hits zero we re-quote — the server is the source of truth, and if
  // the promo stage advanced (e.g. free seats sold out during the read)
  // the user sees the new price with a "Price updated" notice.
  useEffect(() => {
    if (!lockExpiresAt) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [lockExpiresAt]);

  const remainingMs = lockExpiresAt ? Math.max(0, lockExpiresAt - now) : 0;
  const remainingSec = Math.floor(remainingMs / 1000);
  const mm = String(Math.floor(remainingSec / 60)).padStart(2, "0");
  const ss = String(remainingSec % 60).padStart(2, "0");

  useEffect(() => {
    if (lockExpiresAt && remainingMs === 0) {
      refetchQuote();
    }
  }, [remainingMs, lockExpiresAt]);

  useEffect(() => {
    if (authLoading) return;
    if (!user || typeof user !== "object") {
      toast.error(t("agreementUi.loginRequired"));
      navigate(`/login?redirect=/checkout/agreement/${tier}`);
    }
  }, [user, authLoading, navigate, tier, t]);

  const canSubmit = useMemo(
    () => ackRisk && ackNotAdvice && ackNoRefund && fullName.trim().length >= 2 && !submitting,
    [ackRisk, ackNotAdvice, ackNoRefund, fullName, submitting],
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit || !tierValid) return;
    // If a code has been typed but not yet linked, try to link it now
    // silently — never block the checkout on an invalid code, just skip.
    if (refTail.trim() && refState.status !== "linked") {
      try {
        await api.post("/referrals/link", { code: `PMS-${refTail.trim()}` });
      } catch { /* silently ignore — invalid codes don't block checkout */ }
    }
    setSubmitting(true);
    try {
      await api.post("/agreement/sign", {
        tier,
        full_name: fullName.trim(),
        acknowledged_risk: ackRisk,
        acknowledged_no_refund: ackNoRefund,
        acknowledged_not_advice: ackNotAdvice,
      });
      const { data } = await api.post("/checkout/session", {
        package_id: tier,
        origin_url: window.location.origin,
        code: appliedCode || undefined,
      });
      // Clear any pending tier intent — the dashboard banner should not
      // greet the user again after they've moved into the paid funnel.
      try { localStorage.removeItem("summit_intent_tier"); } catch { /* ignore */ }
      toast.success(t("agreementUi.signSuccess"));
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error(t("agreementUi.signError"));
        setSubmitting(false);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("agreementUi.signError"));
      setSubmitting(false);
    }
  };

  if (!tierValid) {
    return (
      <div data-testid={AGREEMENT.root} className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
        <div className="max-w-md text-center bg-white rounded-2xl p-10 shadow-sm border border-slate-200">
          <FileWarning className="mx-auto text-amber-500 mb-4" size={40} />
          <h1 className="font-display font-black text-2xl text-slate-900">{t("agreementUi.invalidTier")}</h1>
          <Link to="/pricing" className="mt-6 inline-block text-blue-800 font-semibold underline">
            {t("agreementUi.backPricing")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={AGREEMENT.root} className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-10 py-10 lg:py-14">
        {/* Header */}
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.28em] text-amber-600 font-semibold mb-3">
            {t("agreementUi.eyebrow")}
          </div>
          <h1 className="font-display font-black text-slate-900 text-4xl sm:text-5xl leading-[1.02] tracking-tight">
            {t("agreementUi.title")}
          </h1>
          <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl">
            {t("agreementUi.subtitle", { tier: tierLabel })}
          </p>
          <div className="mt-4 flex items-center gap-4 text-xs text-slate-500 flex-wrap">
            <span data-testid={AGREEMENT.tierLabel} className="inline-flex items-center gap-1.5 bg-blue-800/10 text-blue-800 px-2.5 py-1 rounded-full font-semibold uppercase tracking-widest">
              <ShieldCheck size={12} /> {tierLabel}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <BadgeInfo size={12} /> {t("agreementUi.versionLabel")}: <span className="font-mono">{version || "…"}</span>
            </span>
          </div>

          {/* Effective price — computed server-side, matches the eventual
              Stripe charge. Shows applied discounts so the user sees exactly
              why the number is what it is BEFORE they sign. */}
          {quote && (
            <div
              data-testid="agreement-quote"
              className="mt-6 bg-slate-50 border border-slate-200 rounded-2xl p-5 sm:p-6 max-w-xl"
            >
              <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-semibold">
                {t("agreementUi.yourPriceToday")}
              </div>
              <div className="mt-2 flex items-baseline gap-3 flex-wrap">
                {quote.base_amount !== quote.effective_amount && (
                  <span className="text-slate-400 text-2xl line-through font-semibold">
                    ${quote.base_amount.toLocaleString()}
                  </span>
                )}
                <span className={`font-display font-black tracking-tighter ${
                  quote.effective_amount === 0
                    ? "text-emerald-600 text-4xl sm:text-5xl"
                    : "text-slate-900 text-4xl sm:text-5xl"
                }`}>
                  ${quote.effective_amount.toLocaleString(undefined, { minimumFractionDigits: Number.isInteger(quote.effective_amount) ? 0 : 2, maximumFractionDigits: 2 })}
                </span>
                <span className="text-sm text-slate-500">
                  {quote.effective_amount === 0 ? t("agreementUi.fullyCovered") : t("agreementUi.oneTime")}
                </span>
              </div>
              {Array.isArray(quote.reasons) && quote.reasons.length > 0 && (
                <ul className="mt-4 space-y-1.5">
                  {quote.reasons.map((r) => translateReason(r, t)).map((r, i) => (
                    <li
                      key={i}
                      data-testid="agreement-quote-reason"
                      className="text-xs text-slate-600 flex items-start gap-2"
                    >
                      <ShieldCheck size={12} className="text-emerald-600 shrink-0 mt-0.5" />
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              )}
              <PromoCodeBox tier={tier} applied={appliedCode} quoteUrl={quoteUrl}
                onApplied={(code, data) => { setAppliedCode(code); setQuote(data); setLockExpiresAt(Date.now() + PRICE_LOCK_SECONDS * 1000); }} />
              {quote.effective_amount === 0 && (
                <p className="mt-4 text-[11px] text-emerald-700 font-medium">
                  {t("agreementUi.noCardCharged")}
                </p>
              )}

              {/* Price lock countdown — the launch promo has a hard seat cap.
                  We tell the user their price is locked for 15 minutes so the
                  countdown creates honest urgency. When it expires we re-quote
                  server-side; if the promo stage advanced we surface a subtle
                  "Price updated" notice instead of a silent change. */}
              <div
                data-testid="agreement-price-lock"
                className={`mt-5 flex items-center gap-3 rounded-xl border px-4 py-3 ${
                  remainingSec > 60
                    ? "bg-blue-50 border-blue-100"
                    : "bg-amber-50 border-amber-200"
                }`}
              >
                <Timer size={16} className={remainingSec > 60 ? "text-blue-800" : "text-amber-700"} />
                <div className="flex-1 text-xs">
                  <div className={`font-semibold ${remainingSec > 60 ? "text-slate-900" : "text-amber-900"}`}>
                    {remainingSec > 0 ? t("agreementUi.priceLocked") : t("agreementUi.refreshingPrice")}
                    {remainingSec > 0 && (
                      <span
                        data-testid="agreement-price-lock-timer"
                        className="ml-2 font-mono font-bold text-slate-900"
                      >
                        {mm}:{ss}
                      </span>
                    )}
                  </div>
                  <div className={`mt-0.5 ${remainingSec > 60 ? "text-slate-600" : "text-amber-800"}`}>
                    {remainingSec > 60
                      ? t("agreementUi.completeBeforeTimer")
                      : remainingSec > 0
                      ? t("agreementUi.almostUp")
                      : t("agreementUi.pricesRequoted")}
                  </div>
                </div>
              </div>

              {priceChangedNotice && (
                <div
                  data-testid="agreement-price-updated"
                  className="mt-3 text-[11px] rounded-md bg-slate-100 border border-slate-200 px-3 py-2 text-slate-700"
                >
                  {t("agreementUi.priceChanged")}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Legal body */}
        <div
          data-testid={AGREEMENT.scrollBody}
          className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-10 space-y-8 text-slate-700 leading-relaxed"
        >
          <RiskCallout icon={<AlertTriangle size={18} />} title={t("agreementUi.sectionRisk")}>
            {t("agreementUi.riskBody")}
          </RiskCallout>

          <Section title={t("agreementUi.sectionNotAdvice")}>
            {t("agreementUi.notAdviceBody")}
          </Section>

          <Section title={t("agreementUi.sectionNoRefund")} highlight>
            {t("agreementUi.noRefundBody")}
            <div className="mt-4 rounded-lg border-2 border-amber-500 bg-amber-50/50 p-3 text-slate-900">
              <div className="text-[10px] uppercase tracking-widest font-bold text-amber-800">
                {t("agreementUi.referralClauseTitle")}
              </div>
              <div className="mt-1 text-sm leading-snug">
                {t("agreementUi.referralClauseBody")}
              </div>
            </div>
            <div className="mt-4">
              <Link
                to="/register"
                data-testid={AGREEMENT.freeAccountLink}
                className="inline-flex items-center gap-2 text-blue-800 font-semibold underline decoration-amber-400 underline-offset-4 hover:text-blue-900"
              >
                → {t("agreementUi.freeAccountCta")}
              </Link>
            </div>
          </Section>

          <Section title={t("agreementUi.sectionLiability")}>
            {t("agreementUi.liabilityBody")}
          </Section>
        </div>

        {/* Signature block */}
        <form onSubmit={submit} className="mt-8 bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-10">
          <div className="flex items-center gap-2 text-blue-800 font-semibold text-sm uppercase tracking-widest mb-2">
            <Lock size={14} /> {t("agreementUi.sectionSignature")}
          </div>
          <p className="text-slate-600 text-sm max-w-2xl">{t("agreementUi.signatureIntro")}</p>

          <div className="mt-6 space-y-4">
            <CheckRow
              testId={AGREEMENT.checkRisk}
              checked={ackRisk}
              onChange={setAckRisk}
              label={t("agreementUi.checkRisk")}
            />
            <CheckRow
              testId={AGREEMENT.checkNotAdvice}
              checked={ackNotAdvice}
              onChange={setAckNotAdvice}
              label={t("agreementUi.checkNotAdvice")}
            />
            <CheckRow
              testId={AGREEMENT.checkNoRefund}
              checked={ackNoRefund}
              onChange={setAckNoRefund}
              label={t("agreementUi.checkNoRefund")}
            />
          </div>

          <div className="mt-8">
            <Label htmlFor="full-name" className="text-slate-900 font-semibold">
              {t("agreementUi.fullNameLabel")}
            </Label>
            <Input
              id="full-name"
              data-testid={AGREEMENT.fullNameInput}
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder={t("agreementUi.fullNamePlaceholder")}
              autoComplete="name"
              className="mt-2 h-12 text-lg font-display italic"
            />
            <p className="mt-2 text-xs text-slate-500">
              {user?.email ? `${t("agreementUi.versionLabel")}: ${version} · ${user.email}` : ""}
            </p>
          </div>

          {/* Peak referral code — pre-fixed with a locked `PMS-` badge so
              the user only types the 4-char tail. Optional field; if left
              blank the checkout proceeds normally. Never blocks even if
              invalid — worst case the code is ignored. */}
          <div className="mt-6">
            <Label htmlFor="peak-code" className="text-slate-900 font-semibold flex items-center gap-2">
              {t("agreementUi.friendReferYou")}
              <span className="text-xs font-normal text-slate-500">({t("agreementUi.optional")})</span>
            </Label>
            <div className="mt-2 flex items-stretch max-w-sm">
              <div className="inline-flex items-center px-3 rounded-l-md bg-slate-100 border border-r-0 border-slate-300 text-slate-500 font-mono font-bold tracking-wider select-none">
                PMS-
              </div>
              <Input
                id="peak-code"
                data-testid="referral-checkout-input"
                value={refTail}
                onChange={(e) => {
                  const clean = e.target.value.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 8);
                  setRefTail(clean);
                  if (refState.status !== "idle") setRefState({ status: "idle" });
                }}
                onBlur={async () => {
                  const tail = refTail.trim();
                  if (!tail) return;
                  setRefState({ status: "linking" });
                  try {
                    const { data } = await api.get(`/referrals/lookup/${encodeURIComponent(`PMS-${tail}`)}`);
                    if (data.valid) {
                      setRefState({ status: "linked", name: data.referrer_name });
                    } else {
                      setRefState({ status: "error", message: t("agreementUi.noMemberCode") });
                    }
                  } catch {
                    setRefState({ status: "error", message: t("agreementUi.couldntVerify") });
                  }
                }}
                placeholder={t("agreementUi.codePlaceholder")}
                className="rounded-l-none h-11 font-mono uppercase tracking-widest"
              />
            </div>
            {refState.status === "linked" && (
              <p data-testid="referral-checkout-linked" className="mt-2 text-sm text-emerald-700 font-semibold flex items-center gap-1.5">
                <ShieldCheck size={14} /> {t("agreementUi.linkedTo", { name: refState.name })}
              </p>
            )}
            {refState.status === "error" && (
              <p data-testid="referral-checkout-error" className="mt-2 text-sm text-rose-600">
                {refState.message}
              </p>
            )}
            {refState.status === "idle" && refTail.length > 0 && (
              <p className="mt-2 text-xs text-slate-500">{t("agreementUi.linkOnContinue")}</p>
            )}
          </div>

          <div className="mt-8 flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              data-testid={AGREEMENT.cancelButton}
              onClick={() => navigate("/pricing")}
              className="h-12 rounded-md border-slate-300 text-slate-700"
            >
              {t("agreementUi.cancelButton")}
            </Button>
            <Button
              type="submit"
              data-testid={AGREEMENT.signButton}
              disabled={!canSubmit}
              className="h-12 rounded-md bg-blue-800 hover:bg-blue-700 disabled:bg-slate-300 disabled:text-slate-500 text-white px-8 font-semibold"
            >
              {submitting ? t("agreementUi.signingIn") : t("agreementUi.signButton")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Section({ title, children, highlight = false }) {
  return (
    <section>
      <h2 className={`font-display font-bold text-lg mb-2 ${highlight ? "text-amber-700" : "text-slate-900"}`}>
        {title}
      </h2>
      <div className={`text-sm sm:text-base ${highlight ? "bg-amber-50 border-l-4 border-amber-400 pl-4 py-3 rounded-r-md" : ""}`}>
        {children}
      </div>
    </section>
  );
}

function RiskCallout({ icon, title, children }) {
  return (
    <section className="bg-blue-800/5 border-l-4 border-blue-800 pl-5 py-4 rounded-r-md">
      <h2 className="flex items-center gap-2 font-display font-bold text-lg text-blue-900 mb-2">
        <span className="text-blue-800">{icon}</span> {title}
      </h2>
      <div className="text-sm sm:text-base text-slate-700">{children}</div>
    </section>
  );
}

function CheckRow({ testId, checked, onChange, label }) {
  return (
    <label className="flex items-start gap-3 text-slate-800 cursor-pointer">
      <Checkbox
        data-testid={testId}
        checked={checked}
        onCheckedChange={(v) => onChange(!!v)}
        className="mt-1 border-slate-400 data-[state=checked]:bg-blue-800 data-[state=checked]:border-blue-800"
      />
      <span className="text-sm sm:text-base leading-snug">{label}</span>
    </label>
  );
}
